import { ceil, difference, isEqual, max, min, sortBy } from "lodash";
import React, { ComponentProps, useCallback, useContext, useMemo } from "react";
import { Line } from "react-chartjs-2";
import { Option } from "space-monad";
import styled from "styled-components";
import { chartColours } from "../../../../styles/colours";
import { device } from "../../../../styles/media";
import { getOption } from "../../../../utils/utils";
import { StakeContext } from "../context";
import { StyledTextWidget } from "./tile";

const StyledTile = styled(StyledTextWidget)`
  @media ${device.laptop} {
    grid-column-start: 2;
    grid-column-end: 4;
  }
  align-items: stretch;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: stretch;
  align-items: center;
  width: 100%;
  height: 100%;
  padding: 1em 0;
`;

interface IStakedChartProps { }

const STEP_SIZE = 7;

const StakedChart: React.FunctionComponent<IStakedChartProps> = (props) => {
  const { currentContract } = useContext(StakeContext);

  const sortedList = useMemo(
    () =>
      currentContract.map((contract) =>
        sortBy(contract.stakingTimeline, ([date]) => date.unix())
      ),
    [currentContract]
  );

  const labels = useMemo(
    () => sortedList.map((s) => s.map(([date]) => date.format("DD/MM"))),
    [sortedList]
  );

  const minimum = useMemo(
    () =>
      sortedList.fold(
        () => 0,
        (sorted) => min(sorted.map(([, staked]) => staked))
      ),
    [sortedList]
  );

  const maximum = useMemo(
    () =>
      ceil(
        sortedList.fold(
          () => 0,
          (sorted) => max(sorted.map(([, staked]) => staked)) || 0
        ) + 100000,
        -5
      ),
    [sortedList]
  );

  const chartData: Option<ComponentProps<typeof Line>["data"]> = useMemo(
    () =>
      Option.all([sortedList, labels]).map(([sorted, labels]) => ({
        labels: labels.map((_, i) => i),
        datasets: [
          {
            label: "Locked",
            data: sorted.map(([, staked]) => staked),
            fill: { target: "start", above: chartColours.blue.light },
            borderColor: chartColours.blue.main,
            pointRadius: 1,
          },
        ],
      })),
    [sortedList, labels]
  );

  return chartData.fold(
    () => null,
    (chart) => (
      <StyledTile title="Locked Tokens Over Time" center>
        <InnerChart
          chart={chart}
          labels={labels}
          minimum={minimum}
          maximum={maximum}
        />
      </StyledTile>
    )
  );
};

const InnerChart: React.FC<{
  chart: ComponentProps<typeof Line>["data"];
  labels: Option<string[]>;
  minimum?: number;
  maximum?: number;
}> = React.memo(
  ({ chart, labels, minimum, maximum }) => {
    const getLabel = useCallback(
      (_, index) => labels.map((lab) => lab[index * STEP_SIZE]).getOrElse(""),
      [labels]
    );

    const getLabelForTooltip = useCallback(
      ([
        {
          parsed: { x },
        },
      ]) => labels.map((lab) => lab[x]).getOrElse(""),
      [labels]
    );

    return (
      <Wrapper>
        <Line
          data={chart}
          options={{
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  title: getLabelForTooltip,
                },
              },
            },
            elements: {
              line: {
                tension: 0.4,
              },
            },
            scales: {
              y: {
                min: minimum,
                max: maximum,
              },
              x: {
                type: "linear",
                ticks: {
                  stepSize: STEP_SIZE,
                  callback: getLabel,
                },
              },
            },
          }}
        />
      </Wrapper>
    );
  },
  (props, nextProps) =>
    props.minimum === props.minimum &&
    props.maximum === props.maximum &&
    difference(props.labels.getOrElse([]), nextProps.labels.getOrElse([]))
      .length < 1 &&
    isEqual(props.chart, nextProps.chart)
);

export default StakedChart;
